import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import toast from "react-hot-toast";
import SaveToBoardModal from "../components/SaveToBoardModal.js";
import NewNavbar from "../components/NewNavbar.js";
import ProductCard from "../components/ProductCard.js";
import ProfileMeasurementsForm from "../components/ProfileMeasurementsForm.js";
import HomeProfilePopupFlow from "../components/HomeProfilePopupFlow.js";
import AuthModal from "../components/AuthModal.js";
import StableMasonryGrid from "../components/StableMasonryGrid.js";
import Footer from "../components/Footer.js";
import { ReactComponent as ItemNotFoundIcon } from "./../assets/svgs/item-not-found.svg";
import { PRICE_STEPS } from "../components/PriceSlider";
import { formatProductPrice, getVisibleCardMatchPercentage } from "../utils.js";
import {
  useAestheticInformationCachedQuery,
  useAestheticProductsCachedQuery,
  usePublicMarketplaceSearchCachedQuery,
  useUserMarketplaceSearchCachedQuery,
} from "../store/api.ts";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 500;
const LOAD_MORE_DELAY_MS = 250;

const MAX_PRICE_STEP = PRICE_STEPS[PRICE_STEPS.length - 1];

const createDefaultFilters = () => ({
  size: "",
  fit: "",
  occasion: "",
  color: "",
  minPrice: 0,
  maxPrice: MAX_PRICE_STEP,
});

const parseNumberParam = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const imageHeightClasses = [
  "h-[220px] sm:h-[310px]",
  "h-[240px] sm:h-[340px]",
  "h-[260px] sm:h-[370px]",
  "h-[280px] sm:h-[400px]",
];

const getProductImageHeightClass = (item, index) => {
  const source = String(item?.id ?? item?.name ?? index);
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = source.charCodeAt(i) + ((hash << 5) - hash);
  }
  const safeIndex = Math.abs(hash) % imageHeightClasses.length;
  return imageHeightClasses[safeIndex];
};

const getCategoryGridIdentity = (product, index) => {
  const identityCandidates = [
    product?.id,
    product?.marketplace_product_id,
    product?.marketplaceProductId,
    product?.product_id,
    product?.real_slug,
    product?.slug,
    product?.product_url,
    product?.url,
    product?.image_url,
    product?.image,
  ];

  const stableIdentity = identityCandidates.find((value) => {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === "number" && !Number.isFinite(value)) {
      return false;
    }
    return String(value).trim() !== "";
  });

  return `${
    stableIdentity ??
    product?.name ??
    product?.product_name ??
    "category-product"
  }-${index}`;
};

const normalizeCategoryProduct = (product) => {
  const productSlug = product?.real_slug || product?.slug || "";
  const matchPercentage = product?.match_percentage ??
    product?.matchPercentage ??
    product?.product_score ??
    null;

  return {
    ...product,
    id:
      product?.id ??
      product?.marketplace_product_id ??
      product?.product_id ??
      null,
    name: product?.product_name || product?.name || "Product",
    brand: product?.brand || "",
    slug: productSlug,
    real_slug: productSlug,
    url: product?.product_url || product?.url || "",
    product_url: product?.product_url || product?.url || "",
    image: product?.image_url || product?.image || "",
    image_url: product?.image_url || product?.image || "",
    price: formatProductPrice(product?.price),
    garmentType: product?.garment_type || product?.garmentType || "",
    garment_type: product?.garment_type || product?.garmentType || "",
    matchPercentage,
    match_percentage: matchPercentage,
    productType: "marketplace",
    product_type: "marketplace",
    is_marketplace: true,
  };
};

const getAuthStatus = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return Boolean(window.localStorage.getItem("authToken"));
};

const CategorySidebarProductSkeleton = ({ index }) => {
  const imageHeightClass = index % 2 === 0 ? "h-[340px]" : "h-[370px]";

  return (
    <div className="min-w-0 animate-pulse" aria-hidden="true">
      <div
        className={`
          w-full rounded-[8px]
          bg-[#ECEDEF]
          ${imageHeightClass}
        `}
      />
      <div
        className="
          mt-2 h-[28px] w-full
          rounded-[5px] bg-[#ECEDEF]
        "
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="h-3 w-2/3 rounded bg-[#ECEDEF]" />
        <div className="h-3 w-12 rounded bg-[#ECEDEF]" />
      </div>
    </div>
  );
};

const CategoryPage = () => {
  const navigate = useNavigate();
  const { slug: routeSlug = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const slug = useMemo(() => {
    try {
      return decodeURIComponent(routeSlug).trim().toLowerCase();
    } catch {
      return String(routeSlug).trim().toLowerCase();
    }
  }, [routeSlug]);

  const [isAuthenticated, setIsAuthenticated] = useState(getAuthStatus);

  useEffect(() => {
    const syncAuthentication = () => {
      setIsAuthenticated(getAuthStatus());
    };
    window.addEventListener("eveness-auth-changed", syncAuthentication);
    window.addEventListener("storage", syncAuthentication);
    return () => {
      window.removeEventListener("eveness-auth-changed", syncAuthentication);
      window.removeEventListener("storage", syncAuthentication);
    };
  }, []);

  const initialSearchText = searchParams.get("q") ?? "";
  const initialCategory = searchParams.get("category") ?? "All";
  const initialSustainability = searchParams.get("sustainability") ?? "";
  const initialAppliedFilters = {
    size: searchParams.get("size") ?? "",
    fit: searchParams.get("fit") ?? "",
    occasion: searchParams.get("occasion") ?? "",
    color: searchParams.get("color") ?? "",
    minPrice: searchParams.has("minPrice")
      ? parseNumberParam(searchParams.get("minPrice"), 0)
      : 0,
    maxPrice: searchParams.has("maxPrice")
      ? parseNumberParam(searchParams.get("maxPrice"), MAX_PRICE_STEP)
      : MAX_PRICE_STEP,
  };

  const [searchText, setSearchText] = useState(initialSearchText);
  const [debouncedSearchText, setDebouncedSearchText] = useState(initialSearchText.trim());
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedSustainability, setSelectedSustainability] = useState(initialSustainability);
  const [draftFilters, setDraftFilters] = useState(() => ({...initialAppliedFilters}));
  const [appliedFilters, setAppliedFilters] = useState(() => ({...initialAppliedFilters}));

  const [searchPagination, setSearchPagination] = useState({
    signature: "",
    page: 1,
  });

  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  const loadMoreTimeoutRef = useRef(null);
  const loadMoreLockRef = useRef(false);
  const [surveyProfile, setSurveyProfile] = useState(null);
  const [surveyFlowOpen, setSurveyFlowOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("login");
  const [saveBoardProduct, setSaveBoardProduct] = useState(null);
  const [isSaveBoardModalOpen, setIsSaveBoardModalOpen] = useState(false);

  const {
    data: category,
    isFetching: isCategoryFetching,
    isError: isCategoryError,
    error: categoryError,
  } = useAestheticInformationCachedQuery(
    {
      slug,
    },
    {
      skip: !slug,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  );

  const {
    data: rawProducts = [],
    isFetching: areProductsFetching,
    isError: areProductsError,
  } = useAestheticProductsCachedQuery(
    {
      slug,
    },
    {
      skip: !slug,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [searchText]);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    const normalizedSearchText = searchText.trim();

    if (normalizedSearchText) {
      nextParams.set("q", normalizedSearchText);
    }
    if (selectedCategory && selectedCategory !== "All") {
      nextParams.set("category", selectedCategory);
    }
    if (selectedSustainability) {
      nextParams.set("sustainability", selectedSustainability);
    }
    if (appliedFilters.size) {
      nextParams.set("size", appliedFilters.size);
    }
    if (appliedFilters.fit) {
      nextParams.set("fit", appliedFilters.fit);
    }
    if (appliedFilters.occasion) {
      nextParams.set("occasion", appliedFilters.occasion);
    }
    if (appliedFilters.color) {
      nextParams.set("color", appliedFilters.color);
    }
    if (appliedFilters.minPrice !== null && appliedFilters.minPrice > 0) {
      nextParams.set("minPrice", String(appliedFilters.minPrice));
    }
    if (appliedFilters.maxPrice !== null && appliedFilters.maxPrice < MAX_PRICE_STEP) {
      nextParams.set("maxPrice", String(appliedFilters.maxPrice));
    }

    const currentParams = searchParams.toString();
    const updatedParams = nextParams.toString();
    if (currentParams !== updatedParams) {
      setSearchParams(nextParams, {
        replace: true,
      });
    }
  }, [
    searchText,
    selectedCategory,
    selectedSustainability,
    appliedFilters,
    searchParams,
    setSearchParams,
  ]);

  const hasAppliedAdvancedFilters = Boolean(
    appliedFilters.size ||
      appliedFilters.fit ||
      appliedFilters.occasion ||
      appliedFilters.color ||
      appliedFilters.minPrice > 0 ||
      appliedFilters.maxPrice < MAX_PRICE_STEP,
  );

  const hasActiveSearchCriteria = Boolean(
    debouncedSearchText ||
      selectedCategory !== "All" ||
      selectedSustainability ||
      hasAppliedAdvancedFilters,
  );

  const searchSignature = useMemo(
    () =>
      JSON.stringify({
        slug,
        auth: isAuthenticated ? "user" : "public",
        text: debouncedSearchText,
        category: selectedCategory,
        sustainability: selectedSustainability,
        filters: appliedFilters,
      }),
    [
      slug,
      isAuthenticated,
      debouncedSearchText,
      selectedCategory,
      selectedSustainability,
      appliedFilters,
    ],
  );

  const searchPage = searchPagination.signature === searchSignature ? searchPagination.page : 1;
  useEffect(() => {
    setSearchPagination({
      signature: searchSignature,

      page: 1,
    });
  }, [searchSignature]);

  const marketplaceSearchArgs = useMemo(
    () => ({
      text: debouncedSearchText,
      page: searchPage,
      page_size: PAGE_SIZE,
      aesthetic: slug,
      categoriesType: selectedCategory !== "All" ? selectedCategory : undefined,
      sustainability: selectedSustainability || undefined,
      sizeType: appliedFilters.size || undefined,
      fitType: appliedFilters.fit || undefined,
      occasionType: appliedFilters.occasion || undefined,
      color: appliedFilters.color || undefined,
      minPrice: appliedFilters.minPrice > 0 ? appliedFilters.minPrice : undefined,
      maxPrice: appliedFilters.maxPrice < MAX_PRICE_STEP
          ? appliedFilters.maxPrice
          : undefined,
    }),
    [
      debouncedSearchText,
      searchPage,
      slug,
      selectedCategory,
      selectedSustainability,
      appliedFilters,
    ],
  );

  const publicSearchResult = usePublicMarketplaceSearchCachedQuery(
    marketplaceSearchArgs,
    {
      skip: !slug || isAuthenticated || !hasActiveSearchCriteria,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  );

  const userSearchResult = useUserMarketplaceSearchCachedQuery(
    marketplaceSearchArgs,
    {
      skip: !slug || !isAuthenticated || !hasActiveSearchCriteria,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  );

  const activeSearchResult = isAuthenticated
    ? userSearchResult
    : publicSearchResult;

  const categoryProducts = useMemo(
    () => rawProducts.map(normalizeCategoryProduct),
    [rawProducts],
  );

  const visibleCategoryProducts = useMemo(
    () => categoryProducts.slice(0, page * PAGE_SIZE),
    [categoryProducts, page],
  );

  const activeSearchData = activeSearchResult.currentData;

  const searchProducts = useMemo(() => {
    const responseProducts = Array.isArray(activeSearchData?.products)
      ? activeSearchData.products
      : [];

    return responseProducts.map(normalizeCategoryProduct);
  }, [activeSearchData?.products]);

  const visibleProducts = hasActiveSearchCriteria
    ? searchProducts
    : visibleCategoryProducts;

  const categoryProductsHaveMore = visibleCategoryProducts.length < categoryProducts.length;
  const searchTotal = Number(activeSearchData?.total_products ?? activeSearchData?.total ?? 0);
  const searchNextPageRaw = Number(activeSearchData?.next_page);
  const searchHasMore = activeSearchData?.has_more === true ||
    (Number.isFinite(searchNextPageRaw) && searchNextPageRaw > searchPage) ||
    (searchTotal > 0 && searchProducts.length < searchTotal);

  const searchNextPage = Number.isFinite(searchNextPageRaw) && searchNextPageRaw > searchPage
      ? searchNextPageRaw
      : searchPage + 1;

  const hasMoreProducts = hasActiveSearchCriteria
    ? searchHasMore
    : categoryProductsHaveMore;

  const isFilterProductsLoading = hasActiveSearchCriteria &&
    searchPage === 1 &&
    activeSearchResult.isFetching;

  const isLoadingMoreSearchProducts = hasActiveSearchCriteria && searchPage > 1 && activeSearchResult.isFetching;
  const productsError = hasActiveSearchCriteria
    ? activeSearchResult.isError
    : areProductsError;
  const isLoadingMoreProducts = hasActiveSearchCriteria
    ? isLoadingMoreSearchProducts
    : isLoadingMore;
  const isInitialSearchLoading =
    hasActiveSearchCriteria &&
    (activeSearchResult.isLoading ||
      (activeSearchResult.isFetching &&
        searchPage === 1 &&
        searchProducts.length === 0));

  const isInitialProductsLoading = hasActiveSearchCriteria
    ? isInitialSearchLoading
    : areProductsFetching && categoryProducts.length === 0;

  const shouldShowPrimaryProductsLoading = isFilterProductsLoading || isInitialProductsLoading;

  const sidebarProductCount = !isAuthenticated ? 3 : 2;
  const sidebarProducts = useMemo(
    () => visibleProducts.slice(0, sidebarProductCount),
    [visibleProducts, sidebarProductCount],
  );
  const desktopGridProducts = useMemo(
    () => visibleProducts.slice(sidebarProductCount),
    [visibleProducts, sidebarProductCount],
  );

  const mobileMasonryItems = useMemo(
    () =>
      visibleProducts.map((product, index) => ({
        id: getCategoryGridIdentity(product, index),
        product,
        productIndex: index,
      })),
    [visibleProducts],
  );

  const desktopMasonryItems = useMemo(
    () =>
      desktopGridProducts.map((product, index) => {
        const originalProductIndex = index + sidebarProductCount;
        return {
          id: getCategoryGridIdentity(product, originalProductIndex),
          product,
          productIndex: originalProductIndex,
        };
      }),
    [desktopGridProducts, sidebarProductCount],
  );

  useEffect(() => {
    if (loadMoreTimeoutRef.current) {
      window.clearTimeout(loadMoreTimeoutRef.current);
      loadMoreTimeoutRef.current = null;
    }
    loadMoreLockRef.current = false;
    setIsLoadingMore(false);
    setPage(1);
  }, [slug]);

  useEffect(() => {
    return () => {
      if (loadMoreTimeoutRef.current) {
        window.clearTimeout(loadMoreTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!hasActiveSearchCriteria) return;
    if (activeSearchResult.isFetching) return;
    if (activeSearchResult.isError) return;
    if (searchProducts.length === 0) return;
    if (!searchHasMore) return;
    if (typeof IntersectionObserver === "undefined") return;
    const loadMoreNode = loadMoreRef.current;
    if (!loadMoreNode) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.unobserve(entry.target);
        setSearchPagination((current) => {
          const currentPage = current.signature === searchSignature ? current.page : 1;
          if (searchNextPage <= currentPage) return current;

          return {
            signature: searchSignature,
            page: searchNextPage,
          };
        });
      },
      {
        root: null,
        rootMargin: "600px 0px",
        threshold: 0.01,
      },
    );

    observer.observe(loadMoreNode);
    return () => {
      observer.disconnect();
    };
  }, [
    hasActiveSearchCriteria,
    activeSearchResult.isFetching,
    activeSearchResult.isError,
    searchProducts.length,
    searchHasMore,
    searchNextPage,
    searchSignature,
  ]);


  useEffect(() => {
    if (hasActiveSearchCriteria) return;
    if (areProductsFetching) return;
    if (areProductsError)  return;
    if (categoryProducts.length === 0) return;
    if (!categoryProductsHaveMore) return;
    if (isLoadingMore) return;
    if (typeof IntersectionObserver === "undefined") return;
    const loadMoreNode = loadMoreRef.current;
    if (!loadMoreNode) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        if (loadMoreLockRef.current) return;
        loadMoreLockRef.current = true;
        setIsLoadingMore(true);
        observer.unobserve(entry.target);
        loadMoreTimeoutRef.current = window.setTimeout(() => {
          setPage((currentPage) => {
            const totalPages = Math.ceil(categoryProducts.length / PAGE_SIZE);
            return Math.min(currentPage + 1, totalPages);
          });
          setIsLoadingMore(false);
          loadMoreLockRef.current = false;
          loadMoreTimeoutRef.current = null;
        }, LOAD_MORE_DELAY_MS);
      },
      {
        root: null,
        rootMargin: "600px 0px",
        threshold: 0.01,
      },
    );

    observer.observe(loadMoreNode);
    return () => {
      observer.disconnect();
    };
  }, [
    hasActiveSearchCriteria,
    areProductsFetching,
    areProductsError,
    categoryProducts.length,
    categoryProductsHaveMore,
    isLoadingMore,
  ]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      ...draftFilters,
    });
  };

  const handleResetFilters = () => {
    const resetFilters = createDefaultFilters();
    setDraftFilters(resetFilters);
    setAppliedFilters(resetFilters);
  };

  const handleAestheticPageChange = (nextSlug) => {
    const normalizedNextSlug = String(nextSlug || "")
      .trim()
      .toLowerCase();

    if (!normalizedNextSlug) {
      navigate("/marketplace");
      return;
    }

    if (normalizedNextSlug === slug) return;
    const currentSearch = searchParams.toString();
    navigate(
      `/categories/${encodeURIComponent(normalizedNextSlug)}${
        currentSearch ? `?${currentSearch}` : ""
      }`,
    );
  };

  const openAuthentication = (mode) => {
    setSurveyFlowOpen(false);
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  };

  const handleSaveBoard = (item) => {
    if (!isAuthenticated) {
      toast.error("Please sign in to save this product.");
      openAuthentication("login");
      return;
    }
    if (!item) {
      toast.error("Product details are not available yet.");
      return;
    }
    const itemSlug = String(
      item?.real_slug || item?.slug || item?.productSlug || "",
    ).trim();

    if (!itemSlug) {
      toast.error("Product slug is not available yet.");
      return;
    }

    const productType =
      item?.productType ||
      item?.product_type ||
      (item?.is_marketplace === false ? "product" : "marketplace");
    const visibleMatchPercentage = getVisibleCardMatchPercentage(item);
    setSaveBoardProduct({
      ...item,
      slug: itemSlug,
      real_slug: itemSlug,
      productType,
      product_type: productType,
      is_marketplace: productType === "marketplace",
      matchPercentage: visibleMatchPercentage,
      match_percentage: visibleMatchPercentage,
      image: item?.image || item?.image_url || "",
      image_url: item?.image_url || item?.image || "",
      name: item?.name || item?.product_name || "Product",
      garmentType: item?.garmentType || item?.garment_type || "",
      garment_type: item?.garment_type || item?.garmentType || "",
    });
    setIsSaveBoardModalOpen(true);
  };

  const categoryStatus = Number(
    categoryError?.status ||
      categoryError?.originalStatus ||
      categoryError?.data?.status,
  );

  const isCategoryNotFound = isCategoryError && categoryStatus === 404;

  const whyAtEveness = Array.isArray(category?.why_at_eveness)
    ? category.why_at_eveness
    : [];

  const shopWithHeading = category?.shop_with_heading || category?.shop_with_title || "";
  const shopWithDescription = category?.shop_with_description || category?.shop_with_desciption || "";

  if (isCategoryNotFound) {
    return (
      <div className="min-h-screen bg-white">
        <NewNavbar />

        <main className="mx-auto max-w-[1200px] px-5 py-20 text-center">
          <div className="mx-auto flex h-[88px] w-[88px] items-center justify-center rounded-full bg-[#F4F4F4]">
            <ItemNotFoundIcon />
          </div>

          <h1 className="mt-6 text-[30px] font-semibold text-[#171717]">
            Category not found
          </h1>

          <p className="mt-3 text-sm text-[#777777]">
            The category may have been removed or the URL may be incorrect.
          </p>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-[#171717]">
      <Helmet>
        <title>
          {category?.title || category?.name || "Category"} | Eveness
        </title>

        <meta
          name="description"
          content={
            category?.description ||
            `Explore ${category?.name || "this category"} on Eveness.`
          }
        />
      </Helmet>

      <NewNavbar
        bottomNav="marketplace"
        brandName={category?.name || ""}
        searchText={searchText}
        onSearchTextChange={setSearchText}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        selectedAesthetic={slug}
        onAestheticChange={handleAestheticPageChange}
        selectedSustainability={selectedSustainability}
        onSustainabilityChange={setSelectedSustainability}
        draftFilters={draftFilters}
        onDraftFiltersChange={setDraftFilters}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
      />

      <HomeProfilePopupFlow
        isOpen={surveyFlowOpen && !isAuthenticated}
        mode="public"
        initialStep={3}
        initialProfile={surveyProfile}
        autoAnalyzeOnOpen={Boolean(surveyProfile)}
        onClose={() => setSurveyFlowOpen(false)}
        onSignIn={() => openAuthentication("login")}
        onSignUp={() => openAuthentication("signup")}
        isAuthenticated={isAuthenticated}
      />

      <AuthModal
        isOpen={authModalOpen}
        initialMode={authModalMode}
        onClose={() => setAuthModalOpen(false)}
      />

      <SaveToBoardModal
        isOpen={isSaveBoardModalOpen}
        product={saveBoardProduct}
        onClose={() => {
          setIsSaveBoardModalOpen(false);

          setSaveBoardProduct(null);
        }}
        onRequireAuth={() => {
          setIsSaveBoardModalOpen(false);
          setSaveBoardProduct(null);
          openAuthentication("login");
        }}
      />

      <main className="mx-auto max-w-[1480px] px-4 pb-24 pt-5 md:px-5">
        <section className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_240px] 2xl:grid-cols-[minmax(0,1fr)_280px]">
          {/* Left / category information */}
          <div className="min-w-0">
            {/* Category hero */}
            <div className="relative h-[340px] overflow-hidden rounded-[6px] bg-[#181818] md:h-[520px]">
              {isCategoryFetching && !category ? (
                <div className="flex h-full items-center justify-center">
                  <div className="flex items-center gap-2 text-sm text-white/70">
                    <span>Loading category...</span>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  </div>
                </div>
              ) : isCategoryError && !category ? (
                <div className="flex h-full items-center justify-center px-6 text-center text-sm text-white/70">
                  Failed to load category information.
                </div>
              ) : (
                <>
                  {/* Background image */}
                  {category?.image_url ? (
                    <>
                      <img
                        src={category.image_url}
                        alt={category?.name || "Category"}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                      {/* Dark overlay so text stays readable */}
                      <div className="absolute inset-0 bg-black/55" />
                    </>
                  ) : (
                    <div className="absolute inset-0 bg-[#1B1B1B]" />
                  )}
                  {/* Hero content */}
                  <div className="relative z-[1] flex h-full items-center justify-center px-6">
                    <div className="flex flex-col items-center text-center">
                      {/* Category name */}
                      <h1 className="font-inter text-[34px] font-medium uppercase leading-none tracking-[-0.02em] text-white sm:text-[44px]">
                        {category?.name || slug}
                      </h1>

                      {/* Marketplace */}
                      <p className="mt-[1px] font-spaceMono text-[11px] uppercase tracking-[0.32em] text-white opacity-[45%] sm:text-[13px]">
                        EVENESS MARKETPLACE
                      </p>
                      {/* Divider */}
                      <div className="mt-5 h-px w-[76px] bg-white/15" />
                      {/* Established */}
                      <p className="mt-5 font-spaceMono text-[11px] tracking-[0.08em] text-white/45 sm:text-[13px]">
                        Est. 2024
                      </p>
                      {/* Location */}
                      <p className="mt-5 font-spaceMono text-[10px] tracking-[0.04em] text-white/45 sm:text-[12px]">
                        Los Angeles, CA &amp; London, UK
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Information + guest survey */}
            <div
              className={[
                "mt-5 grid grid-cols-1 items-start gap-5",
                !isAuthenticated
                  ? "lg:grid-cols-[minmax(0,1fr)_minmax(360px,440px)]"
                  : "",
              ].join(" ")}
            >
              <article className="bg-white px-5 py-6 md:px-8">
                {/* Category title + description */}
                <section>
                  {category?.title ? (
                    <h2 className="font-helvena text-[30px] font-bold leading-[1.15] text-black sm:text-[36px]">
                      {category.title}
                    </h2>
                  ) : null}
                  {category?.description ? (
                    <p className="mt-5 font-helvena text-[14px] font-normal leading-[160%] text-[#555555]">
                      {category.description}
                    </p>
                  ) : null}
                </section>
                {/* Why at Eveness */}
                {whyAtEveness.length > 0 ? (
                  <section
                    className={
                      category?.title || category?.description
                        ? "mt-7 border-t border-[#EEEEEE] pt-7"
                        : ""
                    }
                  >
                    <h2 className="font-helvena text-[22px] font-bold text-[#111111]">
                      Why at Eveness
                    </h2>
                    <ul className="mt-5 list-disc space-y-4 pl-5">
                      {whyAtEveness.map((item, index) => (
                        <li
                          key={`${item?.title || "reason"}-${index}`}
                          className="font-helvena text-[14px] leading-6 text-[#555555]"
                        >
                          {item?.title ? (
                            <strong className="font-semibold text-[#111111]">
                              {item.title}:{" "}
                            </strong>
                          ) : null}

                          {item?.description || ""}
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                {/* Shop with intention */}
                {shopWithHeading || shopWithDescription ? (
                  <section
                    className={[
                      whyAtEveness.length > 0
                        ? "mt-6 border-t border-[#EEEEEE] pt-6"
                        : "",
                    ].join(" ")}
                  >
                    {shopWithHeading ? (
                      <h2 className="font-helvena text-[22px] font-bold text-[#111111]">
                        {shopWithHeading}
                      </h2>
                    ) : null}
                    {shopWithDescription ? (
                      <p className="mt-4 whitespace-pre-line font-helvena text-[14px] font-normal leading-6 text-[#555555]">
                        {shopWithDescription}
                      </p>
                    ) : null}
                  </section>
                ) : null}
              </article>

              {/* Guest personalization */}
              {!isAuthenticated ? (
                <div className="min-w-0 self-start">
                  <ProfileMeasurementsForm
                    variant="details"
                    initialProfile={surveyProfile}
                    showSignInText={false}
                    containerClassName="border border-[#E7E0DB]"
                    onSignUpLogin={() => openAuthentication("login")}
                    onAnalyze={(profile) => {
                      setSurveyProfile(profile);

                      setSurveyFlowOpen(true);
                    }}
                  />
                </div>
              ) : null}
            </div>
          </div>

          {/* Desktop right products */}
          <aside className="hidden min-w-0 xl:block">
            {shouldShowPrimaryProductsLoading ? (
              <div className="grid grid-cols-1 gap-3">
                {Array.from({
                  length: sidebarProductCount,
                }).map((_, index) => (
                  <CategorySidebarProductSkeleton
                    key={`category-sidebar-loader-${index}`}
                    index={index}
                  />
                ))}
              </div>
            ) : sidebarProducts.length > 0 ? (
              <div className="grid grid-cols-1 gap-3">
                {sidebarProducts.map((item, index) => (
                  <div
                    key={`category-sidebar-${
                      item.id ?? item.slug ?? index
                    }-${index}`}
                    className="min-w-0"
                  >
                    <ProductCard
                      item={item}
                      imageHeightClass={
                        index % 2 === 0
                          ? "h-[240px] sm:h-[340px]"
                          : "h-[260px] sm:h-[370px]"
                      }
                      matchPercentage={item.matchPercentage}
                      onSaveBoard={handleSaveBoard}
                      isSavingBoard={false}
                      compactActions
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </aside>
        </section>

        {/* Products */}
        <section className="mt-10">
          {shouldShowPrimaryProductsLoading ? (
            <div className="flex min-h-[180px] items-center justify-center">
              <div className="flex items-center gap-2 text-sm text-[#80808C]">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#D8DADF] border-t-[#111111]" />

                <span>Loading products...</span>
              </div>
            </div>
          ) : productsError ? (
            <p className="mt-6 text-sm text-red-500">
              Failed to load category products.
            </p>
          ) : visibleProducts.length === 0 ? (
            <div className="mx-auto w-full max-w-[520px] text-center">
              <div className="mx-auto flex h-[88px] w-[88px] items-center justify-center rounded-full bg-[#F4F4F4]">
                <ItemNotFoundIcon />
              </div>

              <h2 className="mt-6 font-helvetica text-[28px] font-semibold text-[#0F0F0F] sm:text-[34px]">
                No products found.
              </h2>
            </div>
          ) : (
            <>
              {/* Mobile + tablet */}
              <div className="xl:hidden">
                <StableMasonryGrid
                  items={mobileMasonryItems}
                  className="mt-5"
                  keyPrefix={`category-${slug}-mobile`}
                  mobileColumns={2}
                  tabletColumns={3}
                  desktopColumns={3}
                  wideColumns={3}
                  renderItem={(gridItem) => (
                    <ProductCard
                      item={gridItem.product}
                      imageHeightClass={getProductImageHeightClass(
                        gridItem.product,
                        gridItem.productIndex,
                      )}
                      matchPercentage={gridItem.product.matchPercentage}
                      onSaveBoard={handleSaveBoard}
                      isSavingBoard={false}
                    />
                  )}
                />
              </div>

              {/* Desktop masonry */}
              {desktopGridProducts.length > 0 ? (
                <div className="hidden xl:block">
                  <StableMasonryGrid
                    items={desktopMasonryItems}
                    className="mt-5"
                    keyPrefix={`category-${slug}-desktop`}
                    mobileColumns={2}
                    tabletColumns={3}
                    desktopColumns={4}
                    wideColumns={5}
                    renderItem={(gridItem) => (
                      <ProductCard
                        item={gridItem.product}
                        imageHeightClass={getProductImageHeightClass(
                          gridItem.product,
                          gridItem.productIndex,
                        )}
                        matchPercentage={gridItem.product.matchPercentage}
                        onSaveBoard={handleSaveBoard}
                        isSavingBoard={false}
                      />
                    )}
                  />
                </div>
              ) : null}
            </>
          )}

          {!shouldShowPrimaryProductsLoading &&
          !productsError &&
          visibleProducts.length > 0 &&
          (hasMoreProducts || isLoadingMoreProducts) ? (
            <div
              ref={loadMoreRef}
              className="flex min-h-[80px] items-center justify-center"
              aria-live="polite"
              aria-busy={isLoadingMoreProducts}
            >
              {isLoadingMoreProducts ? (
                <div className="flex items-center gap-2 text-sm text-[#80808C]">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#D8DADF] border-t-[#111111]" />
                  <span>Loading more products...</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CategoryPage;
